import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import SendIcon from '@mui/icons-material/Send';
import {
  inventoryCountService,
  ReconciliationResponse,
} from '../../../../core/modules/inventoryCount/services/api';

const STATUS_LABEL: Record<string, string> = {
  draft: 'Borrador',
  active: 'Activa',
  closed: 'Cerrada',
  cancelled: 'Cancelada',
};

const SummaryStat = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <Grid item xs={6} sm={3}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="h6" fontWeight={600}>
      {value}
    </Typography>
  </Grid>
);

const ReconciliationScreen = () => {
  const { id: companyIdParam, sessionId: sessionIdParam } = useParams<{ id: string; sessionId: string }>();
  const companyId = Number(companyIdParam);
  const sessionId = Number(sessionIdParam);
  const navigate = useNavigate();

  const [data, setData] = useState<ReconciliationResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [sendingWebhook, setSendingWebhook] = useState(false);
  const [info, setInfo] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await inventoryCountService.getReconciliation(companyId, sessionId);
      setData(result);
    } catch (err) {
      setError('No se pudo cargar la conciliación de esta sesión.');
    } finally {
      setLoading(false);
    }
  }, [companyId, sessionId]);

  useEffect(() => {
    if (companyId && sessionId) {
      loadData();
    }
  }, [companyId, sessionId, loadData]);

  const handleDownload = async () => {
    setDownloading(true);
    setError(null);
    try {
      await inventoryCountService.downloadReconciliationExcel(companyId, sessionId);
    } catch (err) {
      setError('No se pudo descargar el Excel de conciliación.');
    } finally {
      setDownloading(false);
    }
  };

  const handleSendWebhook = async () => {
    setSendingWebhook(true);
    setError(null);
    setInfo(null);
    try {
      const result = await inventoryCountService.sendReconciliationWebhook(companyId, sessionId);
      setInfo(result.message);
    } catch (err: any) {
      setError(
        err?.response?.data?.detail ||
          'No se pudo enviar la conciliación por webhook. Verifica que la empresa tenga una URL configurada.'
      );
    } finally {
      setSendingWebhook(false);
    }
  };

  if (loading && !data) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className="container" sx={{ py: 4 }}>
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <IconButton onClick={() => navigate(`/dashboard/inventory-count/${companyId}`)} size="small">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" fontWeight={600}>
          Conciliación: {data?.summary.sessionName ?? '...'}
        </Typography>
        {data && (
          <Chip
            size="small"
            label={STATUS_LABEL[data.summary.status] ?? data.summary.status}
            color={data.summary.status === 'closed' ? 'info' : 'success'}
          />
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {info && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setInfo(null)}>
          {info}
        </Alert>
      )}

      {data && (
        <>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Grid container spacing={2}>
                <SummaryStat label="Bodega" value={data.summary.warehouseName} />
                <SummaryStat
                  label="SKUs contados"
                  value={`${data.summary.countedSkus} / ${data.summary.totalSkus} (${data.summary.percent}%)`}
                />
                <SummaryStat label="Umbral de alerta" value={`${Number(data.summary.varianceThresholdPct)}%`} />
                <SummaryStat
                  label={data.summary.hasClientTheoretical ? 'Variación (operador)' : 'SKUs con variación'}
                  value={
                    data.summary.skusWithVarianceOperator > 0 ? (
                      <Chip size="small" color="warning" label={data.summary.skusWithVarianceOperator} />
                    ) : (
                      '0'
                    )
                  }
                />
                {data.summary.hasClientTheoretical && (
                  <SummaryStat
                    label="Variación (cliente)"
                    value={
                      data.summary.skusWithVarianceClient > 0 ? (
                        <Chip size="small" color="warning" label={data.summary.skusWithVarianceClient} />
                      ) : (
                        '0'
                      )
                    }
                  />
                )}
                <SummaryStat
                  label={data.summary.hasClientTheoretical ? 'Total teórico (operador)' : 'Total teórico'}
                  value={data.summary.totalTheoreticalOperatorQty}
                />
                {data.summary.hasClientTheoretical && (
                  <SummaryStat label="Total teórico (cliente)" value={data.summary.totalTheoreticalClientQty} />
                )}
                <SummaryStat label="Total contado" value={data.summary.totalCountedQty} />
                <SummaryStat
                  label={data.summary.hasClientTheoretical ? 'Diferencia total (operador)' : 'Diferencia total'}
                  value={data.summary.totalDifferenceOperatorQty}
                />
                {data.summary.hasClientTheoretical && (
                  <SummaryStat label="Diferencia total (cliente)" value={data.summary.totalDifferenceClientQty} />
                )}
              </Grid>
            </CardContent>
          </Card>

          <Box display="flex" gap={2} mb={3}>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              disabled={downloading}
              onClick={handleDownload}
            >
              {downloading ? <CircularProgress size={20} /> : 'Descargar Excel'}
            </Button>
            <Button
              variant="outlined"
              startIcon={<SendIcon />}
              disabled={sendingWebhook}
              onClick={handleSendWebhook}
            >
              {sendingWebhook ? <CircularProgress size={20} /> : 'Enviar por webhook'}
            </Button>
          </Box>

          <TableContainer component={Card}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>SKU</TableCell>
                  <TableCell>Producto</TableCell>
                  <TableCell>Ubicación</TableCell>
                  <TableCell align="right">Teórico {data.summary.hasClientTheoretical ? '(operador)' : ''}</TableCell>
                  {data.summary.hasClientTheoretical && (
                    <TableCell align="right">Teórico (cliente)</TableCell>
                  )}
                  <TableCell align="right">Contado</TableCell>
                  <TableCell align="right">Diferencia {data.summary.hasClientTheoretical ? '(operador)' : ''}</TableCell>
                  <TableCell align="right">% Dif. {data.summary.hasClientTheoretical ? '(operador)' : ''}</TableCell>
                  <TableCell>Estado {data.summary.hasClientTheoretical ? '(operador)' : ''}</TableCell>
                  {data.summary.hasClientTheoretical && (
                    <>
                      <TableCell align="right">Diferencia (cliente)</TableCell>
                      <TableCell align="right">% Dif. (cliente)</TableCell>
                      <TableCell>Estado (cliente)</TableCell>
                    </>
                  )}
                  <TableCell>Método</TableCell>
                  <TableCell>Contado por</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.items.map((item) => {
                  const statusChipFor = (exceeds: boolean) =>
                    !item.isCounted
                      ? { label: 'Sin contar', color: 'default' as const }
                      : exceeds
                      ? { label: 'Con variación', color: 'warning' as const }
                      : { label: 'OK', color: 'success' as const };
                  const operatorStatus = statusChipFor(item.exceedsThresholdOperator);
                  const clientStatus = statusChipFor(item.exceedsThresholdClient);
                  return (
                    <TableRow key={item.productId}>
                      <TableCell>{item.sku}</TableCell>
                      <TableCell>{item.productName}</TableCell>
                      <TableCell>{item.location || '—'}</TableCell>
                      <TableCell align="right">{item.theoreticalOperatorQty ?? '—'}</TableCell>
                      {data.summary.hasClientTheoretical && (
                        <TableCell align="right">{item.theoreticalClientQty ?? '—'}</TableCell>
                      )}
                      <TableCell align="right">{item.countedQty ?? '—'}</TableCell>
                      <TableCell align="right">{item.differenceOperatorQty ?? '—'}</TableCell>
                      <TableCell align="right">
                        {item.differenceOperatorPct !== null ? `${item.differenceOperatorPct.toFixed(1)}%` : '—'}
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={operatorStatus.label} color={operatorStatus.color} />
                      </TableCell>
                      {data.summary.hasClientTheoretical && (
                        <>
                          <TableCell align="right">{item.differenceClientQty ?? '—'}</TableCell>
                          <TableCell align="right">
                            {item.differenceClientPct !== null ? `${item.differenceClientPct.toFixed(1)}%` : '—'}
                          </TableCell>
                          <TableCell>
                            <Chip size="small" label={clientStatus.label} color={clientStatus.color} />
                          </TableCell>
                        </>
                      )}
                      <TableCell>{item.method ?? '—'}</TableCell>
                      <TableCell>{item.countedByName || item.countedByEmail || '—'}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
};

export default ReconciliationScreen;
